import styled from '@emotion/styled'
import React from 'react'
import { Hugeicon } from '../../../../icons/Hugeicon'

interface CalendarPromoCardProps {
  title?: string
  ctaText?: string
  ctaHref?: string
}

const CalendarPromoCard: React.FC<CalendarPromoCardProps> = ({
  title = 'Already Posting Events on Your Own Website?',
  ctaText = 'Get your calendar',
  ctaHref = '/dash/business-calendar',
}) => (
  <PromoCard>
    <PromoHeader>
      <PromoTitle>{title}</PromoTitle>
    </PromoHeader>
    <PromoBody>
      <PromoLeft>
        <PromoText>
          Stop duplicating your work. Post once with CivicLift and your event automatically
          appears on your site <PromoHighlight>and</PromoHighlight> across our network of community calendars.
        </PromoText>
        <PromoLearnMore href={ctaHref}>
          {ctaText}
          <ArrowIcon />
        </PromoLearnMore>
      </PromoLeft>
      <PromoRight>
        <NetworkSvg viewBox="0 0 300 140" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="pathGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(147, 197, 253, 0.6)" />
              <stop offset="100%" stopColor="rgba(147, 197, 253, 0.25)" />
            </linearGradient>
            <linearGradient id="sourceGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#2563eb" />
            </linearGradient>
          </defs>

          {/* Curved paths */}
          <path id="pathTop" d="M 70 65 C 115 65, 140 30, 170 30" fill="none" stroke="url(#pathGradient)" strokeWidth="2" />
          <path id="pathMid" d="M 70 65 C 115 65, 140 70, 170 70" fill="none" stroke="url(#pathGradient)" strokeWidth="2" />
          <path id="pathBot" d="M 70 65 C 115 65, 140 110, 170 110" fill="none" stroke="url(#pathGradient)" strokeWidth="2" />

          {/* Source icon - CivicLift kite */}
          <image href="/img/email-kite-logo.png" x="20" y="40" width="50" height="50" />
          <text x="45" y="105" textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize="9" fontWeight="600" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Post Once
          </text>

          {/* Destination: Your Website */}
          <rect x="170" y="15" width="30" height="30" rx="7" fill="url(#sourceGradient)" />
          <g transform="translate(177, 22)">
            <rect x="1" y="1" width="14" height="14" rx="1.5" stroke="white" strokeWidth="1.3" fill="none" />
            <line x1="1" y1="4.5" x2="15" y2="4.5" stroke="white" strokeWidth="1.3" />
            <line x1="3.5" y1="7.5" x2="12.5" y2="7.5" stroke="white" strokeWidth="1" strokeLinecap="round" />
            <line x1="3.5" y1="10" x2="10" y2="10" stroke="white" strokeWidth="1" strokeLinecap="round" />
          </g>
          <text x="208" y="35" fill="rgba(255,255,255,0.9)" fontSize="11" fontWeight="500">Your Website</text>

          {/* Destination: Local Calendar */}
          <rect x="170" y="55" width="30" height="30" rx="7" fill="rgba(255,255,255,0.12)" />
          <g transform="translate(177, 62)">
            <circle cx="5" cy="4" r="2.5" stroke="white" strokeWidth="1.3" fill="none" />
            <circle cx="12" cy="6" r="2" stroke="white" strokeWidth="1.3" fill="none" />
            <path d="M0 15V13.5C0 11.6 1.6 10 3.5 10H7C8.9 10 10.5 11.6 10.5 13.5V15" stroke="white" strokeWidth="1.3" strokeLinecap="round" fill="none" />
          </g>
          <text x="208" y="75" fill="rgba(255,255,255,0.8)" fontSize="11" fontWeight="500">Local Calendar</text>

          {/* Destination: Community Site */}
          <rect x="170" y="95" width="30" height="30" rx="7" fill="rgba(255,255,255,0.12)" />
          <g transform="translate(177, 102)">
            <circle cx="5" cy="4" r="2.5" stroke="white" strokeWidth="1.3" fill="none" />
            <circle cx="12" cy="6" r="2" stroke="white" strokeWidth="1.3" fill="none" />
            <path d="M0 15V13.5C0 11.6 1.6 10 3.5 10H7C8.9 10 10.5 11.6 10.5 13.5V15" stroke="white" strokeWidth="1.3" strokeLinecap="round" fill="none" />
          </g>
          <text x="208" y="115" fill="rgba(255,255,255,0.8)" fontSize="11" fontWeight="500">Community Site</text>

          {/* Animated dots */}
          <circle r="4" fill="#93c5fd" style={{ filter: 'drop-shadow(0 0 3px rgba(147, 197, 253, 0.8))' }}>
            <animateMotion dur="2.5s" repeatCount="indefinite" begin="0s">
              <mpath href="#pathTop" />
            </animateMotion>
          </circle>
          <circle r="4" fill="#93c5fd" style={{ filter: 'drop-shadow(0 0 3px rgba(147, 197, 253, 0.8))' }}>
            <animateMotion dur="2.5s" repeatCount="indefinite" begin="0.4s">
              <mpath href="#pathMid" />
            </animateMotion>
          </circle>
          <circle r="4" fill="#93c5fd" style={{ filter: 'drop-shadow(0 0 3px rgba(147, 197, 253, 0.8))' }}>
            <animateMotion dur="2.5s" repeatCount="indefinite" begin="0.8s">
              <mpath href="#pathBot" />
            </animateMotion>
          </circle>
        </NetworkSvg>
      </PromoRight>
    </PromoBody>
  </PromoCard>
)

export default CalendarPromoCard

const ArrowIcon: React.FC = () => <Hugeicon name="arrow-right-long" size={16} />

const PromoCard = styled.div`
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%);
  border-radius: 16px;
  padding: 32px;
  gap: 8px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 60%);
    pointer-events: none;
  }

  @media (max-width: 680px) {
    padding: 24px;
  }
`

const PromoHeader = styled.div`
  width: 100%;
`

const PromoBody = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 680px) {
    flex-direction: column;
  }
`

const PromoLeft = styled.div`
  flex: 1;
  min-width: 0;
`

const PromoRight = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 680px) {
    width: 100%;
    max-width: 320px;
  }
`

const PromoTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: white;
  margin: 0;
  padding: 0;
  line-height: 1.3;
`

const PromoText = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  margin: 0 0 20px;
  line-height: 1.6;
`

const PromoHighlight = styled.span`
  color: #93c5fd;
  font-weight: 600;
  text-decoration: underline;
`

const PromoLearnMore = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  padding: 10px 18px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border-radius: 8px;
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
    color: white;
  }
`

const NetworkSvg = styled.svg`
  width: 100%;
  height: auto;
  display: block;
`
